/**
 * LoadToolboxModal (#638).
 *
 * Lists the user's saved toolbox profiles (own + the global default) in My /
 * Global tabs. Clicking a profile makes it the active toolbox. Custom profiles
 * can be renamed (inline) or deleted; the permanent profiles — the global
 * default and the per-user 'User Default' — expose no rename/delete controls
 * (the backend enforces this too). Mirrors LoadPresetModal's portal + tabs and
 * reuses its SCSS; rows and the My-Toolbox tab use the toolbox icon.
 *
 * Reads everything from ToolboxProfileContext — no direct fetching. Rename and
 * delete are performed without blocking window dialogs.
 */

import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faToolbox, faTrash, faPen, faCheck, faLayerGroup, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { useToolboxProfile } from '../../../context/ToolboxProfileContext';
import { USER_DEFAULT_NAME, type ToolboxProfile } from '../../../types/toolboxProfile';
import '../../layerDrawer/modals/LoadPresetModal.scss';
import AppModal from '../../../components/common/AppModal';

interface LoadToolboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

/** Permanent profiles (global default + the per-user 'User Default') can't be renamed or deleted. */
function isPermanentProfile(p: ToolboxProfile): boolean {
  return p.is_global || p.name === USER_DEFAULT_NAME;
}

export const LoadToolboxModal: React.FC<LoadToolboxModalProps> = ({ isOpen, onClose }) => {
  const { profiles, setActiveProfile, update, remove } = useToolboxProfile();
  const [activeTab, setActiveTab] = useState<'my' | 'global'>('my');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const filtered = profiles.filter((p) => (activeTab === 'global' ? p.is_global : !p.is_global));

  const handleLoad = useCallback(
    (profile: ToolboxProfile) => {
      setActiveProfile(profile.id);
      onClose();
    },
    [setActiveProfile, onClose],
  );

  const handleDelete = useCallback(
    async (profile: ToolboxProfile, e: React.MouseEvent) => {
      e.stopPropagation();
      setDeletingId(profile.id);
      setError(null);
      try {
        await remove(profile.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete toolbox');
      } finally {
        setDeletingId(null);
      }
    },
    [remove],
  );

  const startRename = useCallback((profile: ToolboxProfile, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(profile.id);
    setRenameValue(profile.name);
    setError(null);
  }, []);

  const confirmRename = useCallback(
    async (profile: ToolboxProfile) => {
      const name = renameValue.trim();
      if (!name || name === profile.name) {
        setRenamingId(null);
        return;
      }
      try {
        await update(profile.id, { name });
        setRenamingId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to rename toolbox');
      }
    },
    [renameValue, update],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <AppModal
      isOpen
      onClose={onClose}
      width={520}
      maxHeight="90vh"
      header={<h3>Load Toolbox</h3>}
    >
      {/* Keydown stays on this wrapper rather than the removed overlay: the handler
          was never reached by focus, only by events bubbling up from the controls
          inside, and this element still receives those. */}
      <div className="load-preset-modal" onKeyDown={handleKeyDown}>
        <div className="preset-tabs">
          <button
            className={`preset-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            <FontAwesomeIcon icon={faToolbox} />
            <span>My Toolboxes</span>
          </button>
          <button
            className={`preset-tab ${activeTab === 'global' ? 'active' : ''}`}
            onClick={() => setActiveTab('global')}
          >
            <FontAwesomeIcon icon={faGlobe} />
            <span>Global</span>
          </button>
        </div>

        <div className="load-preset-modal-body">
          {error && <div className="load-preset-error">{error}</div>}

          {filtered.length === 0 ? (
            <div className="preset-empty">
              <FontAwesomeIcon icon={faLayerGroup} />
              <span>
                {activeTab === 'my'
                  ? "You haven't saved any toolboxes yet."
                  : 'No global toolbox available.'}
              </span>
            </div>
          ) : (
            <div className="preset-list">
              {filtered.map((profile) => {
                const permanent = isPermanentProfile(profile);
                const renaming = renamingId === profile.id;
                return (
                  <div key={profile.id} className="preset-item" onClick={() => !renaming && handleLoad(profile)}>
                    <div className="preset-item-icon">
                      <FontAwesomeIcon icon={faToolbox} />
                    </div>
                    <div className="preset-item-content">
                      {renaming ? (
                        <input
                          className="toolbox-rename-input"
                          type="text"
                          value={renameValue}
                          autoFocus
                          maxLength={255}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') confirmRename(profile);
                            else if (e.key === 'Escape') setRenamingId(null);
                          }}
                        />
                      ) : (
                        <div className="preset-item-name">{profile.name}</div>
                      )}
                      <div className="preset-item-meta">
                        <span className="layer-count">{profile.profile_data.tools.length} tools</span>
                      </div>
                    </div>
                    {!permanent && (
                      <div className="preset-item-actions">
                        {renaming ? (
                          <button
                            className="action-btn"
                            title="Confirm rename"
                            aria-label="Confirm rename"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmRename(profile);
                            }}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        ) : (
                          <button
                            className="action-btn"
                            title="Rename toolbox"
                            aria-label="Rename toolbox"
                            onClick={(e) => startRename(profile, e)}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        )}
                        <button
                          className="action-btn delete-btn"
                          title="Delete toolbox"
                          aria-label="Delete toolbox"
                          onClick={(e) => handleDelete(profile, e)}
                          disabled={deletingId === profile.id}
                        >
                          <FontAwesomeIcon icon={deletingId === profile.id ? faSyncAlt : faTrash} spin={deletingId === profile.id} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="load-preset-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </AppModal>
  );
};
